/**
 * Baccarat bead-plate ("珠盤路") history panel.
 *
 * A left-edge slide-out panel with a vertical "HISTORY" tab. Collapsed by
 * default — only the tab pokes out at the screen edge; tapping it slides the
 * panel in. Shows the 96 most-recent matches in a 16-column × 6-row grid that
 * fills column-major (top-to-bottom, then the next column to the right), the
 * traditional bead-plate order.
 *
 * Each cell is a coloured circle with a letter:
 *   Player → green "P"   Banker → red "B"   Tie → blue "T"
 * plus optional pair dots:
 *   banker pair → red dot, top-left      player pair → blue dot, bottom-right
 *
 * A legend row across the top tallies totals per result and per pair.
 *
 * Pure DOM, mounted in #dom-hud (so it scales with the stage) and driven by
 * {@link MatchHistoryManager}. Follows the GameLogWidget pattern.
 */
import { MatchHistoryManager, MATCH_HISTORY_MAX } from '../game/matchHistoryManager';
import type { MatchHistoryEntry } from '../game/matchHistoryManager';
import type { GameResult } from '../types/game';

const LETTER: Record<GameResult, string> = {
  player: 'P',
  banker: 'B',
  tie: 'T',
};

export class HistoryPanel {
  private readonly root: HTMLElement;
  private readonly tab: HTMLElement;
  private readonly cells: HTMLElement[] = [];
  private readonly legendNums: Record<string, HTMLElement> = {};
  private isExpanded = false;

  constructor() {
    const host = document.getElementById('dom-hud');
    if (!host) throw new Error('HistoryPanel: #dom-hud missing');

    this.root = document.createElement('div');
    this.root.className = 'history-widget';

    // -------- Panel (grid + legend) --------
    const panel = document.createElement('div');
    panel.className = 'history-panel';

    panel.appendChild(this.buildLegend());

    const grid = document.createElement('div');
    grid.className = 'history-grid';
    for (let i = 0; i < MATCH_HISTORY_MAX; i++) {
      const cell = document.createElement('div');
      cell.className = 'hist-cell empty';

      const letter = document.createElement('span');
      letter.className = 'hist-letter';

      const bpair = document.createElement('span');
      bpair.className = 'hist-dot bpair';
      const ppair = document.createElement('span');
      ppair.className = 'hist-dot ppair';

      cell.append(letter, bpair, ppair);
      grid.appendChild(cell);
      this.cells.push(cell);
    }
    panel.appendChild(grid);

    // -------- Vertical HISTORY tab --------
    this.tab = document.createElement('button');
    this.tab.className = 'history-tab';
    this.tab.setAttribute('aria-label', 'Toggle match history');
    const tabLabel = document.createElement('span');
    tabLabel.className = 'history-tab-label';
    tabLabel.textContent = 'HISTORY';
    this.tab.appendChild(tabLabel);
    this.tab.addEventListener('click', () => this.toggle());

    // Panel first, tab to its right — so when collapsed the whole widget
    // slides left by the panel width and only the tab stays at the edge.
    this.root.append(panel, this.tab);
    host.appendChild(this.root);

    MatchHistoryManager.on(() => this.render());
    this.render();
  }

  toggle(): void {
    this.isExpanded = !this.isExpanded;
    this.root.classList.toggle('expanded', this.isExpanded);
    this.tab.classList.toggle('active', this.isExpanded);
  }

  collapse(): void {
    if (!this.isExpanded) return;
    this.toggle();
  }

  private buildLegend(): HTMLElement {
    const legend = document.createElement('div');
    legend.className = 'history-legend';
    // key, icon-class, letter (empty for pair/total icons).
    const items: Array<[string, string, string]> = [
      ['total', 'total', ''],
      ['player', 'player', 'P'],
      ['banker', 'banker', 'B'],
      ['tie', 'tie', 'T'],
      ['playerPair', 'ppair', ''],
      ['bankerPair', 'bpair', ''],
    ];
    for (const [key, iconClass, letter] of items) {
      const item = document.createElement('span');
      item.className = 'hl-item';

      const icon = document.createElement('span');
      icon.className = `hl-icon hl-${iconClass}`;
      if (letter) icon.textContent = letter;

      const num = document.createElement('span');
      num.className = 'hl-num';
      num.textContent = '0';
      this.legendNums[key] = num;

      item.append(icon, num);
      legend.appendChild(item);
    }
    return legend;
  }

  private render(): void {
    const entries = MatchHistoryManager.entries;

    for (let i = 0; i < this.cells.length; i++) {
      const cell = this.cells[i];
      const entry: MatchHistoryEntry | undefined = entries[i];
      const letter = cell.firstChild as HTMLElement;
      if (!entry) {
        cell.className = 'hist-cell empty';
        letter.textContent = '';
        continue;
      }
      cell.className =
        `hist-cell ${entry.result}` +
        (entry.bankerPair ? ' has-bpair' : '') +
        (entry.playerPair ? ' has-ppair' : '');
      letter.textContent = LETTER[entry.result];
    }

    const c = MatchHistoryManager.counts;
    this.legendNums.total.textContent = String(c.total);
    this.legendNums.player.textContent = String(c.player);
    this.legendNums.banker.textContent = String(c.banker);
    this.legendNums.tie.textContent = String(c.tie);
    this.legendNums.playerPair.textContent = String(c.playerPair);
    this.legendNums.bankerPair.textContent = String(c.bankerPair);
  }
}
